import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  startDiagnostic,
  submitDiagnostic,
  type StartDiagnosticResponse,
  type SubmitDiagnosticResponse,
} from "../api";
import { Card, Shell } from "../ui";

type State =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "answering"; attempt: StartDiagnosticResponse }
  | { phase: "submitting"; attempt: StartDiagnosticResponse }
  | { phase: "result"; result: SubmitDiagnosticResponse };

const btnStyle: CSSProperties = {
  padding: "0.5rem 1.1rem",
  borderRadius: 8,
  border: "none",
  background: "#1a1a2e",
  color: "#fff",
  fontSize: "0.9rem",
  cursor: "pointer",
};

const PHASE_COPY = {
  pre: {
    title: "Quick check-in",
    intro:
      "A few questions before you start — there's no pass/fail, this just helps measure how much the programme moves the needle. Answer as best you can.",
  },
  post: {
    title: "Quick check-out",
    intro:
      "The same kind of questions as before you started — again, no pass/fail. Your real result comes from the assessment next.",
  },
} as const;

export function DiagnosticView() {
  const { trackId, phase: rawPhase } = useParams<{ trackId: string; phase: string }>();
  const nav = useNavigate();
  const phase = rawPhase === "post" ? "post" : "pre";
  const continueTo = phase === "pre" ? `/learn/tracks/${trackId}` : `/learn/assessments/${trackId}`;
  const navLinks =
    phase === "pre" ? (
      <Link to="/">← My Learning</Link>
    ) : (
      <>
        <Link to={`/learn/tracks/${trackId}`}>← Programme</Link>
        {" · "}
        <Link to="/">My Learning</Link>
      </>
    );

  const [state, setState] = useState<State>({ phase: "loading" });
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!trackId) return;
    setState({ phase: "loading" });
    setAnswers({});
    startDiagnostic(trackId, phase)
      .then((attempt) => setState({ phase: "answering", attempt }))
      .catch((e: unknown) =>
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) }),
      );
  }, [trackId, phase]);

  function toggleAnswer(questionId: string, optionId: string, multi: boolean) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        const next = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
        return { ...prev, [questionId]: next };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  function submit() {
    if (state.phase !== "answering") return;
    const attempt = state.attempt;
    setState({ phase: "submitting", attempt });
    const responses = attempt.questions.map((q) => ({
      questionId: q.id,
      selectedOptionIds: answers[q.id] ?? [],
    }));
    submitDiagnostic(attempt.attemptId, responses)
      .then((result) => setState({ phase: "result", result }))
      .catch((e: unknown) =>
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) }),
      );
  }

  if (state.phase === "loading")
    return (
      <Shell title="Loading…" nav={navLinks}>
        {null}
      </Shell>
    );
  if (state.phase === "error")
    return (
      <Shell title={PHASE_COPY[phase].title} nav={navLinks}>
        <Card>{state.message}</Card>
      </Shell>
    );

  if (state.phase === "result") {
    const r = state.result;
    return (
      <Shell title={PHASE_COPY[phase].title} nav={navLinks}>
        <Card>
          <p style={{ margin: 0, fontSize: "1.05rem", color: "#0a7d33" }}>Thanks — recorded.</p>
        </Card>
        {r.responses && (
          <>
            {r.responses.map((resp, i) => (
              <Card key={resp.questionId}>
                <strong style={{ color: resp.correct ? "#0a7d33" : "#b00020" }}>
                  Q{i + 1}: {resp.correct ? "Correct" : "Incorrect"}
                </strong>
                {resp.explanation && (
                  <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: 0 }}>
                    {resp.explanation}
                  </p>
                )}
              </Card>
            ))}
          </>
        )}
        <p style={{ marginTop: "1.5rem" }}>
          <button type="button" style={btnStyle} onClick={() => nav(continueTo)}>
            {phase === "pre" ? "Continue to the programme" : "Continue to the assessment"}
          </button>
        </p>
      </Shell>
    );
  }

  const attempt = state.attempt;
  const allAnswered = attempt.questions.every((q) => (answers[q.id] ?? []).length > 0);

  return (
    <Shell
      title={PHASE_COPY[phase].title}
      subtitle={`${attempt.questions.length} questions`}
      nav={navLinks}
    >
      <Card>
        <p style={{ margin: 0, color: "#555", fontSize: "0.9rem" }}>{PHASE_COPY[phase].intro}</p>
      </Card>
      {attempt.questions.map((q, i) => {
        const multi = q.type === "MULTI_SELECT";
        return (
          <Card key={q.id}>
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              {i + 1}. {q.stem}
            </p>
            {multi && (
              <p
                style={{
                  marginTop: "-0.5rem",
                  marginBottom: "0.5rem",
                  color: "#666",
                  fontSize: "0.85rem",
                }}
              >
                Select all that apply
              </p>
            )}
            {q.options.map((opt) => (
              <label key={opt.id} style={{ display: "block", padding: "0.25rem 0" }}>
                <input
                  type={multi ? "checkbox" : "radio"}
                  name={q.id}
                  checked={(answers[q.id] ?? []).includes(opt.id)}
                  onChange={() => toggleAnswer(q.id, opt.id, multi)}
                />{" "}
                {opt.text}
              </label>
            ))}
          </Card>
        );
      })}
      <p style={{ marginTop: "1.5rem" }}>
        <button
          type="button"
          style={{ ...btnStyle, opacity: state.phase === "submitting" ? 0.6 : 1 }}
          disabled={!allAnswered || state.phase === "submitting"}
          onClick={submit}
        >
          {state.phase === "submitting" ? "Submitting…" : "Submit"}
        </button>
        {!allAnswered && (
          <span style={{ marginLeft: "0.75rem", color: "#888", fontSize: "0.85rem" }}>
            Answer every question to submit.
          </span>
        )}
        {!attempt.required && (
          <button
            type="button"
            onClick={() => nav(continueTo)}
            style={{
              marginLeft: "0.75rem",
              border: "none",
              background: "none",
              color: "#666",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Skip for now
          </button>
        )}
      </p>
    </Shell>
  );
}
